import { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useTheme } from '@/contexts/ThemeContext/useTheme';
import { Theme } from '@/contexts/ThemeContext/ThemeContext';
import sunWithFace from '@/assets/emojis/sun-with-face.svg';
import sunWithFaceDark from '@/assets/emojis/sun-with-face_dark.svg';
import newMoon from '@/assets/emojis/new-moon.svg';
import { cn } from '@/lib/utils';

/**
 * Design names the themes after times of day: Morning = light, Midnight = dark.
 * The sun darkens a step down the gold ramp in dark mode; the moon is the same artwork in both.
 */
const getMoods = (theme: Theme): { theme: Theme; label: string; icon: string }[] => [
    { theme: 'light', label: 'Morning', icon: theme === 'dark' ? sunWithFaceDark : sunWithFace },
    { theme: 'dark', label: 'Midnight', icon: newMoon },
];

/** The mood options sit on the accordion's own fill, so their hover is a step darker than it. */
const MOOD_PILL_HOVER_CLASSES = 'light:hover:bg-gz-tan-500 dark:hover:bg-gz-blue-800';

/** Light mode fills the whole row on hover. */
const MOOD_ROW_HOVER_CLASSES = 'light:hover:bg-gz-tan-500';

/**
 * Dark mode leaves the row alone and lights the chevron instead (a row fill would land on the
 * panel's own colour and vanish). `group-hover` so it responds to the whole row, not just the icon.
 */
const CHEVRON_HOVER_CLASSES = 'dark:group-hover:bg-gz-blue-600';

const FOCUS_CLASSES = [
    'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-secondary-accent/50',
    'light:focus-visible:ring-gz-marine-700/50 dark:focus-visible:ring-gz-marine-500/50',
].join(' ');

export const MoodSection = () => {
    const { theme, setTheme } = useTheme();
    const [open, setOpen] = useState(false);

    return (
        <Collapsible
            open={open}
            onOpenChange={setOpen}
            className={cn(
                'w-full light:bg-gz-tan-250 dark:bg-gz-blue-900',
                open ? 'rounded-[18px]' : 'rounded-full',
            )}
        >
            <CollapsibleTrigger
                className={cn(
                    'group flex w-full items-center justify-between px-4 py-2 transition-colors',
                    'text-[18px] leading-6 dark:text-gz-neutral-100',
                    open ? 'light:text-gz-marine-900' : 'light:text-gz-neutral-900',
                    open ? 'rounded-t-[18px]' : 'rounded-full',
                    MOOD_ROW_HOVER_CLASSES,
                    FOCUS_CLASSES,
                )}
            >
                Mood
                <span
                    aria-hidden
                    className={cn(
                        'material-symbols-rounded size-6 shrink-0 rounded-lg text-center transition-colors',
                        CHEVRON_HOVER_CLASSES,
                    )}
                >
                    {open ? 'expand_less' : 'expand_more'}
                </span>
            </CollapsibleTrigger>
            <CollapsibleContent className="px-4 pb-2">
                <div role="radiogroup" aria-label="Mood" className="flex flex-col gap-2 pt-4">
                    {getMoods(theme).map(mood => (
                        <button
                            key={mood.theme}
                            type="button"
                            role="radio"
                            aria-checked={theme === mood.theme}
                            onClick={() => setTheme(mood.theme)}
                            className={cn(
                                'flex w-full items-center gap-[11px] rounded-full border-2 px-2 py-1 text-left transition-colors',
                                'text-[16px] leading-6 light:text-gz-neutral-900 dark:text-gz-neutral-100',
                                theme === mood.theme
                                    ? 'light:border-gz-marine-700 dark:border-gz-marine-500'
                                    : 'border-transparent',
                                MOOD_PILL_HOVER_CLASSES,
                                FOCUS_CLASSES,
                            )}
                            data-track-click="Mood Selected"
                            data-track-location="menu"
                            data-track-button-name={`nav-mood-${mood.theme}`}
                        >
                            <img src={mood.icon} alt="" className="size-7 shrink-0" />
                            {mood.label}
                        </button>
                    ))}
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
};
